import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { FiRepeat } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import { colors } from '../../theme/colors';
import { getSalesVsRecovery } from '../../utils/dashboardWidgetData';

const MAROON = colors.primary.main;
// Dark gold for the recovery line — validated against maroon on white
// (CVD deltaE 27.9 deutan, both series >= 3:1 contrast on the card surface).
const GOLD = '#B8860B';

const ChartTip = ({ active, payload, label, formatCurrency }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload || {};
  return (
    <TipBox>
      <TipLabel>{label}</TipLabel>
      <TipRow>
        <TipDot $c={MAROON} />
        <TipName>Sales</TipName>
        <TipValue>{formatCurrency(p.sales || 0)}</TipValue>
      </TipRow>
      <TipRow>
        <TipDot $c={GOLD} />
        <TipName>Recovery</TipName>
        <TipValue>{formatCurrency(p.recovery || 0)}</TipValue>
      </TipRow>
    </TipBox>
  );
};

const SalesRecoveryWidget = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const companyId = activeCompany?.id;
  const branchId = activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!companyId) { setLoading(false); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const res = await getSalesVsRecovery(companyId, activeFinancialYear, branchId);
        if (!cancelled) setData(res || null);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, activeFinancialYear?.id, branchId]);

  if (!companyId) return null;

  const hasData = !!data && (data.totalSales !== 0 || data.totalRecovery !== 0);
  const pct = data?.recoveryPct || 0;
  const tone = pct >= 80 ? 'good' : pct >= 50 ? 'warn' : 'bad';
  const outstanding = (data?.totalSales || 0) - (data?.totalRecovery || 0);

  return (
    <Card>
      <Header>
        <HeaderLeft>
          <IconWrap><FiRepeat size={15} /></IconWrap>
          <Title>Sales vs Recovery</Title>
        </HeaderLeft>
        {hasData && <RecoveryBadge $tone={tone}>Recovery {pct}%</RecoveryBadge>}
      </Header>

      {loading ? (
        <div>
          <SkelBlock $w="40%" $h="16px" style={{ marginBottom: 14 }} />
          <SkelBlock $w="100%" $h="260px" style={{ marginBottom: 14 }} />
          <SkelBlock $w="100%" $h="52px" />
        </div>
      ) : !hasData ? (
        <EmptyBox>Nothing to compare yet — sales and receipts will show up here month by month.</EmptyBox>
      ) : (
        <>
          <LegendRow>
            <LegendItem><Swatch $c={MAROON} /> Sales</LegendItem>
            <LegendItem><SwatchLine $c={GOLD} /> Recovery</LegendItem>
          </LegendRow>

          <ChartZone>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={data.series || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="amalRecSalesBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={MAROON} stopOpacity={0.95} />
                    <stop offset="100%" stopColor={MAROON} stopOpacity={0.65} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke={colors.text.secondary}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={12}
                />
                <YAxis
                  stroke={colors.text.secondary}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={62}
                  tickFormatter={(v) => formatCurrency(v, { compact: true })}
                />
                <Tooltip
                  content={<ChartTip formatCurrency={formatCurrency} />}
                  cursor={{ fill: `${MAROON}08` }}
                />
                <Bar
                  dataKey="sales"
                  fill="url(#amalRecSalesBar)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={26}
                />
                <Line
                  type="monotone"
                  dataKey="recovery"
                  stroke={GOLD}
                  strokeWidth={2.5}
                  dot={{ r: 2.5, fill: GOLD, strokeWidth: 0 }}
                  activeDot={{ r: 4.5, strokeWidth: 2, stroke: '#fff' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartZone>

          <Footer>
            <FootCell>
              <FootLabel>Total Sales</FootLabel>
              <FootValue $c={MAROON}>{formatCurrency(data.totalSales || 0)}</FootValue>
            </FootCell>
            <FootCell>
              <FootLabel>Total Recovered</FootLabel>
              <FootValue $c={GOLD}>{formatCurrency(data.totalRecovery || 0)}</FootValue>
            </FootCell>
            <FootCell>
              <FootLabel>Outstanding</FootLabel>
              <FootValue>{formatCurrency(outstanding)}</FootValue>
            </FootCell>
          </Footer>
        </>
      )}
    </Card>
  );
};

export default SalesRecoveryWidget;

// ── styled ──────────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 13px;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; flex-wrap: wrap;
`;
const HeaderLeft = styled.div`
  display: flex; align-items: center; gap: 9px;
`;
const IconWrap = styled.div`
  width: 28px; height: 28px; border-radius: 8px;
  background: ${MAROON}12;
  color: ${MAROON};
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;
const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.text.primary};
`;
const RecoveryBadge = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 99px;
  background: ${props =>
    props.$tone === 'good' ? `${colors.success.main}15`
      : props.$tone === 'warn' ? `${colors.warning.main}20`
        : `${colors.error.main}12`};
  color: ${props =>
    props.$tone === 'good' ? colors.success.dark
      : props.$tone === 'warn' ? colors.warning.dark
        : colors.error.dark};
`;
const LegendRow = styled.div`
  display: flex; align-items: center; gap: 16px;
`;
const LegendItem = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${colors.text.secondary};
`;
const Swatch = styled.span`
  width: 10px; height: 10px; border-radius: 3px;
  background: ${props => props.$c};
`;
const SwatchLine = styled.span`
  width: 14px; height: 3px; border-radius: 99px;
  background: ${props => props.$c};
`;
const ChartZone = styled.div`
  width: 100%;
  margin-left: -8px;
`;
const Footer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid ${colors.neutral[200]};
  padding-top: 12px;
  gap: 8px;
`;
const FootCell = styled.div`
  display: flex; flex-direction: column; gap: 3px;
  min-width: 0;

  &:not(:first-child) {
    border-left: 1px solid ${colors.neutral[200]};
    padding-left: 14px;
  }
`;
const FootLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.text.tertiary};
`;
const FootValue = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  color: ${props => props.$c || colors.text.primary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const EmptyBox = styled.div`
  padding: 48px 16px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 500;
  color: ${colors.text.secondary};
`;
const SkelBlock = styled.div`
  width: ${props => props.$w || '100%'};
  height: ${props => props.$h || '16px'};
  border-radius: 8px;
  background: ${colors.neutral[100]};
  animation: ${pulse} 1.4s ease-in-out infinite;
`;
const TipBox = styled.div`
  background: #fff;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 9px 12px;
  min-width: 160px;
`;
const TipLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${colors.text.secondary};
  margin-bottom: 5px;
`;
const TipRow = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 12px;
  & + & { margin-top: 3px; }
`;
const TipDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${props => props.$c};
  flex-shrink: 0;
`;
const TipName = styled.span`
  color: ${colors.text.secondary};
  font-weight: 500;
  flex: 1;
`;
const TipValue = styled.b`
  color: ${colors.text.primary};
  font-weight: 700;
`;
